import type { AiProposalBridgeContext } from "@/ai/context/buildContext";
import type { TimelineProposal, TimelineProposalDiff } from "@/compiler/Timeline/timeline.proposals";

function toFinite(value: unknown, fallback = 0): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function proposalHasKeyword(proposal: TimelineProposal, keywords: string[]): boolean {
  const haystack = `${proposal.rationale ?? ""} ${proposal.source ?? ""}`.toLowerCase();
  return keywords.some((keyword) => haystack.includes(keyword));
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function inferDiffTimeSec(diff: TimelineProposalDiff): number | null {
  switch (diff.kind) {
    case "marker/add":
      return toFinite(diff.marker?.timeSec, NaN);
    case "marker/move":
      return toFinite(diff.toTimeSec, NaN);
    case "range/add": {
      const startSec = toFinite(diff.range?.startSec, NaN);
      const endSec = toFinite(diff.range?.endSec, NaN);
      return Number.isFinite(startSec) && Number.isFinite(endSec) ? (startSec + endSec) / 2 : null;
    }
    case "range/update": {
      const startSec = toFinite(diff.toStartSec, NaN);
      const endSec = toFinite(diff.toEndSec, NaN);
      return Number.isFinite(startSec) && Number.isFinite(endSec) ? (startSec + endSec) / 2 : null;
    }
    default:
      return null;
  }
}

export function inferProposalTimeSec(context: AiProposalBridgeContext, proposal: TimelineProposal): number {
  for (const diff of proposal.diffs ?? []) {
    const candidate = inferDiffTimeSec(diff);
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return clamp(candidate, 0, Math.max(0, context.timelineState.durationSec));
    }
  }

  return context.anchorTimeSec;
}

export function averageConfidence(proposals: TimelineProposal[]): number | null {
  const values = proposals
    .map((proposal) => (typeof proposal.confidence === "number" ? proposal.confidence : null))
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));

  if (values.length === 0) return null;
  return values.reduce((total, value) => total + value, 0) / values.length;
}

export function firstSentence(value: string, fallback: string): string {
  const trimmed = value.trim();
  if (!trimmed) return fallback;

  const sentence = trimmed.split(/(?<=[.!?])\s+/)[0]?.trim();
  return sentence || fallback;
}

export function groupField(group: unknown, field: string): unknown {
  if (!group || typeof group !== "object") return undefined;
  return (group as Record<string, unknown>)[field];
}

export function groupLabel(group: unknown): string {
  return String(groupField(group, "label") ?? groupField(group, "name") ?? groupField(group, "kind") ?? "").trim();
}

export function groupId(group: unknown, fallback: string): string {
  return String(groupField(group, "id") ?? fallback).trim() || fallback;
}

export function groupMidpointSec(group: unknown, fallback: number): number {
  const startSec = toFinite(groupField(group, "startSec"), NaN);
  const endSec = toFinite(groupField(group, "endSec"), NaN);
  if (Number.isFinite(startSec) && Number.isFinite(endSec)) {
    return (startSec + endSec) / 2;
  }

  return fallback;
}